import dayjs from 'dayjs'
import api from '@/api'
import { _surfaceErrorMessage, _surfaceSuccessMessage } from '@/snackbar'
import {
  PLACED,
  CLOSED_WON_ENGAGEMENT_STATUSES,
  ENTITIES,
  PLACEMENT_STATUSES_ENUM,
} from '../constants'
import {
  _getAllPlacedStaffingRolesForAGivenConsultant,
  _isStaffingRoleCurrent,
  _managerNames,
} from '../helpers'

export const getters = {
  getFilteredRolesForStaffingViewById(state) {
    return Object.values(state.staffingRolesById).filter(
      (role) =>
        state.placements.find((p) => p.staffingRoleId === role.id)?.status !==
          PLACEMENT_STATUSES_ENUM.PLACED || dayjs(role.startDate).isAfter(dayjs())
    )
  },

  getCurrentPlacedStaffingRoleByConsultantId(state) {
    return (consultantId) => {
      const roles = _getAllPlacedStaffingRolesForAGivenConsultant(state, consultantId)
      return roles.find(_isStaffingRoleCurrent)
    }
  },

  getAllPastPlacedStaffingRolesByConsultantId(state) {
    return (consultantId) => {
      const pastRoles = _getAllPlacedStaffingRolesForAGivenConsultant(state, consultantId)
      return pastRoles.filter((role) => {
        const engagement = state.engagements.find((e) => e.id === role.engagementId)
        return (
          CLOSED_WON_ENGAGEMENT_STATUSES.includes(engagement?.status) &&
          dayjs().isAfter(dayjs(role.endDate))
        )
      })
    }
  },

  currentPlacedStaffingRoles(state) {
    const placedPlacements = state.placements.filter((p) => p.status === PLACED)
    const staffingRolesForPlacements = placedPlacements.flatMap((placement) =>
      state.staffingRoles.filter((role) => placement.staffingRoleId === role.id)
    )
    return staffingRolesForPlacements.filter(_isStaffingRoleCurrent)
  },

  buildCommaDelimitedSkillNames() {
    return (staffingRole) => {
      const skillNames = staffingRole.skillIds.map((skillId) => {
        const skill = this.getEntityById(ENTITIES.SKILLS, skillId)
        return skill ? skill.name : ''
      })
      return skillNames.join(', ')
    }
  },

  staffingRolesAugmentedFor() {
    return (engagementId) => {
      const staffingRoleForEngagement = this.getEntity(ENTITIES.STAFFING_ROLES).filter(
        (role) => role.engagementId === engagementId
      )
      return staffingRoleForEngagement.map((staffingRole) => ({
        ...staffingRole,
        managerName: _managerNames(staffingRole, this.getEntityById),
        allPossiblePlacements: this.buildAllPossiblePlacementsForStaffingRole(staffingRole),
        skillNamesCommaDelimited: this.buildCommaDelimitedSkillNames(staffingRole),
      }))
    }
  },

  getStaffingRolesByEngagementId() {
    return (engagementId) =>
      this.getEntity(ENTITIES.STAFFING_ROLES).filter((s) => s.engagementId === engagementId)
  },
}

export const actions = {
  async createStaffingRole(staffingRole) {
    try {
      const { placements: newPlacements, ...persistedStaffingRole } =
        await api.createStaffingRole(staffingRole)
      newPlacements.forEach((placement) => this.placements.push(placement))
      this.staffingRoles.push(persistedStaffingRole)
      _surfaceSuccessMessage('Staffing role added!')
    } catch {
      _surfaceErrorMessage('Save failed! Please try again later.')
    }
  },

  async deleteStaffingRole(staffingRoleId) {
    try {
      await api.deleteStaffingRole(staffingRoleId)
      this.staffingRoles = this.staffingRoles.filter((sr) => sr.id !== staffingRoleId)
      this.placements = this.placements.filter((p) => p.staffingRoleId !== staffingRoleId)
      _surfaceSuccessMessage('Staffing role deleted!')
    } catch {
      _surfaceErrorMessage('Could not be deleted!')
    }
  },

  async updateStaffingRole(payload) {
    const oldPlacements = this.placements
    try {
      const { placements: newPlacements, ...staffingRole } = await api.updateStaffingRole(payload)
      this.placements = [
        ...oldPlacements.filter((p) => p.staffingRoleId !== staffingRole.id),
        ...newPlacements,
      ]
      this.staffingRoles = this.staffingRoles.map((sr) =>
        sr.id === staffingRole.id ? { ...sr, ...staffingRole } : sr
      )
      _surfaceSuccessMessage('Staffing role updated!')
    } catch (err) {
      const error = err.data?.error
      _surfaceErrorMessage(`Staffing role not updated: ${error}`)
    }
  },

  async bulkUpdateStaffingRole(payload) {
    try {
      const updatedStaffingRoles = await api.bulkUpdateStaffingRole(payload)
      const { ids } = payload
      this.staffingRoles = this.staffingRoles.map((entity) =>
        ids.includes(entity.id)
          ? { ...entity, ...updatedStaffingRoles.find((role) => role.id === entity.id) }
          : entity
      )
      _surfaceSuccessMessage('Staffing role(s) updated!')
    } catch (errorObject) {
      const { error } = errorObject.data
      _surfaceErrorMessage(`Staffing role(s) not updated: ${error}`)
    }
  },

  async fetchStaffingRolesById() {
    this.staffingRolesById = await api.fetchStaffingRolesById()
  },

  async fetchActiveStaffingRolesById() {
    this.staffingRolesById = await api.fetchActiveStaffingRolesById()
  },
}
